import { c as _c } from "react/compiler-runtime";
import DropsList from "@/components/drops/view/DropsList";
import { WaveDropsReverseContainer } from "@/components/waves/drops/WaveDropsReverseContainer";
import { WaveDropsScrollControls } from "@/components/waves/drops/WaveDropsScrollControls";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { RefObject, Ref } from "react";
type WaveMessagesResult = ReturnType<typeof useVirtualizedWaveDrops>["waveMessages"];
interface WaveDropsMessageListSectionProps {
  readonly waveMessages: WaveMessagesResult;
  readonly dropId: string | null;
  readonly scrollContainerRef: RefObject<HTMLDivElement | null>;
  readonly scrollContainerCallbackRef?: Ref<HTMLDivElement> | undefined;
  readonly bottomAnchorRef: RefObject<HTMLDivElement | null>;
  readonly bottomAnchorCallbackRef?: Ref<HTMLDivElement> | undefined;
  readonly onTopIntersection: () => void;
  readonly onScroll?: (() => void) | undefined;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly queueSerialTarget: (serialNo: number) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly serialTarget: number | null;
  readonly targetDropRef: RefObject<HTMLDivElement | null>;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isAtBottom: boolean;
  readonly scrollToBottom: () => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly pendingCount: number;
  readonly onRevealPending: () => void;
  readonly bottomPaddingClassName?: string | undefined;
  readonly unreadDividerSerialNo?: number | null | undefined;
  readonly unreadCount?: number | undefined;
  readonly boostedDrops?: ApiDrop[] | undefined;
  readonly onBoostedDropClick?: ((serialNo: number) => void) | undefined;
  readonly onScrollToUnread?: ((serialNo: number) => void) | undefined;
  readonly onDismissUnread: () => void;
  readonly autoCollapseSerials?: ReadonlySet<number> | undefined;
}
const MIN_DROPS_FOR_PAGINATION = 25;
export const WaveDropsMessageListSection: React.FC<WaveDropsMessageListSectionProps> = t0 => {
  const $ = _c(42);
  const {
    waveMessages,
    dropId,
    scrollContainerRef,
    scrollContainerCallbackRef,
    bottomAnchorRef,
    bottomAnchorCallbackRef,
    onTopIntersection,
    onScroll,
    onReply,
    queueSerialTarget,
    activeDrop,
    serialTarget,
    targetDropRef,
    onQuoteClick,
    isAtBottom,
    scrollToBottom,
    onDropContentClick,
    pendingCount,
    onRevealPending,
    bottomPaddingClassName,
    unreadDividerSerialNo,
    unreadCount,
    boostedDrops,
    onBoostedDropClick,
    onScrollToUnread,
    onDismissUnread,
    autoCollapseSerials
  } = t0;
  const hasNextPage = !!waveMessages?.hasNextPage && waveMessages.drops.length >= MIN_DROPS_FOR_PAGINATION;
  const containerRef = scrollContainerCallbackRef ?? scrollContainerRef;
  const anchorRef = bottomAnchorCallbackRef ?? bottomAnchorRef;
  const t1 = !!waveMessages?.isLoadingNextPage;
  let t2;
  if ($[0] !== waveMessages?.drops) {
    t2 = waveMessages?.drops ?? [];
    $[0] = waveMessages?.drops;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== activeDrop || $[3] !== autoCollapseSerials || $[4] !== boostedDrops || $[5] !== dropId || $[6] !== onBoostedDropClick || $[7] !== onDropContentClick || $[8] !== onQuoteClick || $[9] !== onReply || $[10] !== queueSerialTarget || $[11] !== scrollContainerRef || $[12] !== serialTarget || $[13] !== t2 || $[14] !== targetDropRef || $[15] !== unreadDividerSerialNo) {
    t3 = <DropsList scrollContainerRef={scrollContainerRef} onReplyClick={queueSerialTarget} drops={t2} showWaveInfo={false} onReply={onReply} showReplyAndQuote={true} activeDrop={activeDrop} serialNo={serialTarget} targetDropRef={targetDropRef} onQuoteClick={onQuoteClick} parentContainerRef={scrollContainerRef} dropViewDropId={dropId} onDropContentClick={onDropContentClick} unreadDividerSerialNo={unreadDividerSerialNo} boostedDrops={boostedDrops} onBoostedDropClick={onBoostedDropClick} autoCollapseSerials={autoCollapseSerials} key="drops-list" />;
    $[2] = activeDrop;
    $[3] = autoCollapseSerials;
    $[4] = boostedDrops;
    $[5] = dropId;
    $[6] = onBoostedDropClick;
    $[7] = onDropContentClick;
    $[8] = onQuoteClick;
    $[9] = onReply;
    $[10] = queueSerialTarget;
    $[11] = scrollContainerRef;
    $[12] = serialTarget;
    $[13] = t2;
    $[14] = targetDropRef;
    $[15] = unreadDividerSerialNo;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      height: "1px"
    };
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== anchorRef) {
    t5 = <div ref={anchorRef} style={t4} />;
    $[18] = anchorRef;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== bottomPaddingClassName || $[21] !== containerRef || $[22] !== hasNextPage || $[23] !== onScroll || $[24] !== onTopIntersection || $[25] !== t1 || $[26] !== t3 || $[27] !== t5) {
    t6 = <WaveDropsReverseContainer ref={containerRef} isFetchingNextPage={t1} hasNextPage={hasNextPage} onTopIntersection={onTopIntersection} bottomPaddingClassName={bottomPaddingClassName} onScroll={onScroll}>{t3}{t5}</WaveDropsReverseContainer>;
    $[20] = bottomPaddingClassName;
    $[21] = containerRef;
    $[22] = hasNextPage;
    $[23] = onScroll;
    $[24] = onTopIntersection;
    $[25] = t1;
    $[26] = t3;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== isAtBottom || $[30] !== onDismissUnread || $[31] !== onRevealPending || $[32] !== onScrollToUnread || $[33] !== pendingCount || $[34] !== scrollContainerRef || $[35] !== scrollToBottom || $[36] !== unreadCount || $[37] !== unreadDividerSerialNo) {
    t7 = onScrollToUnread && <WaveDropsScrollControls unreadDividerSerialNo={unreadDividerSerialNo ?? null} unreadCount={unreadCount} scrollContainerRef={scrollContainerRef} onScrollToUnread={onScrollToUnread} onDismissUnread={onDismissUnread} isAtBottom={isAtBottom} scrollToBottom={scrollToBottom} newMessagesCount={pendingCount} onRevealNewMessages={onRevealPending} />;
    $[29] = isAtBottom;
    $[30] = onDismissUnread;
    $[31] = onRevealPending;
    $[32] = onScrollToUnread;
    $[33] = pendingCount;
    $[34] = scrollContainerRef;
    $[35] = scrollToBottom;
    $[36] = unreadCount;
    $[37] = unreadDividerSerialNo;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== t6 || $[40] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  return t8;
};